<template>
  <div>
    <main class="quote">
      <div v-if="loading" class="loading">
        <div>
          <lottie-animation path="lottie-animation/doc.json"/>
        </div>
      </div>
      <div v-else-if="error" class="error">
        <div>{{ $t('LBL_QUOTE_ERROR') }}</div>
      </div>
      <div v-else>
        <header class="header">
          <div class="header__logo">
            <h1 class="d-inline font-weight-light">
              <a href="#">{{ company }}</a>
            </h1>
          </div>
          <ul class="top-nav">
            <li class="right-nav">
              {{ $t('LBL_QUOTE_CREATE_WITH') }} <span><img src="@/assets/img/ini-logo.png" alt=""> <a href="https://initiative-crm.com/">Initiative CRM</a></span>
            </li>
          </ul>
        </header>

        <section class="quote-container">
          <div :class="{'quote-content': true, 'scanning': isSigningPreviewInProgress}">
            <div class="preview">
              <div v-if="!isMobile && isSigningPreviewInProgress" class="scanning-overlay"></div>
              <ini-content-title :title="$t('LBL_QUOTE_TITLE')" icon="facture"></ini-content-title>
              <object v-if="!isMobile" class="preview__content">
                <iframe v-if="hasSignLink" v-show="!iframeLoading" :src="signLink" width="100%" @load="iframeLoading=false"></iframe>
                <template v-if="!isSigningPreviewReady">
                  <embed type="application/pdf" width="100%" :src="objectUrl" />
                </template>
              </object>
            </div>
            <div v-if="!isSigningPreviewReady" :class="{'action': true, 'mobile': isMobile}">
              <esign-info v-if="isEsigned" class="esign-info" :esign-info="esign"></esign-info>
              <esign-signatory-form v-if="signatoryFormVisible" @submit="sign" :signing-in-progress="isSigningPreviewInProgress" :signatory="esign.signatory" :readonly="isSigningPreviewInProgress"></esign-signatory-form>
              <div v-else>
                <div class="buttons">
                  <ini-button
                      v-if="isEsignAccessible"
                      type="success"
                      @click.native="signatoryFormVisible=true"
                      :disabled="signLinkGenerationInProgress"
                      :class="{ 'cursor-wait': signLinkGenerationInProgress }"
                  >{{ $t('LBL_ACCEPT_AND_SIGN_QUOTE') | uppercase }}</ini-button>
                  <ini-button icon="cloud-download" @click.native="downloadPdf">{{ $t('LBL_QUOTE_DOWNLOAD_PDF') | uppercase }}</ini-button>
                  <ini-button v-if="!isEsigned" icon="email" @click.native="sendFeedback">{{ $t('LBL_QUOTE_SEND_FEEDBACK') | uppercase }}</ini-button>
                </div>
                <div v-if="!isEsigned" class="information">
                  <dl>
                    <dt>{{ $t('LBL_QUOTE_QUOTE_NO') }}</dt>
                    <dd>{{ quoteNo }}</dd>

                    <dt v-if="validtill">{{ $t('LBL_QUOTE_VALID_TILL') }}</dt>
                    <dd v-if="validtill">{{ validtill }}</dd>

                    <dt>{{ $t('LBL_QUOTE_LAST_UPDATE') }}</dt>
                    <dd>{{ lastUpdate }}</dd>

                    <dt>{{ $t('LBL_QUOTE_CONTACT') }}</dt>
                    <dd>{{ assignee }}</dd>
                    <dd v-if="phone">
                      <a :href="'tel:' + phone">{{ phone }}</a>
                    </dd>
                    <dd>
                      <a :href="mailto">{{ email }}</a>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import IniContentTitle from '@/components/ContentTitle'
import Header from '@/layout/Header'
import IniButton from '@/components/Button'
import LottieAnimation from "lottie-vuejs/src/LottieAnimation.vue";
import EsignSignatoryForm from "@/components/Esign/EsignSignatoryForm.vue";
import EsignInfo from "@/components/Esign/EsignInfo.vue";

export default {
  components: {
    EsignSignatoryForm,
    EsignInfo,
    LottieAnimation,
    'main-header': Header,
    'ini-content-title': IniContentTitle,
    'ini-button': IniButton
  },

  props: {
    token: null
  },

  data() {
    return {
      base64: "",
      company: "",
      lastUpdate: "",
      quoteNo: "",
      validtill: "",
      assignee: "",
      phone: "",
      email: "",

      mailto: "",

      isMobile: false,

      objectUrl: "",

      loading: true,
      error: false,

      signLinkGenerationInProgress: false,
      signatoryFormVisible: false,
      signLink: '',
      iframeLoading: true,
      esign: {
        'requested': false,
        'esigned': false,
        'signedDate': '',
        'signatory': {
          email: '',
          firstName: '',
          lastName: '',
          cellPhone: '+33'
        }
      }
    }
  },

  async mounted() {
    let response = await PDFPreviewApi.getPdfPreview(this.token);

    if (!response || !response.success) {
      this.error = true;
    } else {
      this.base64 = response.result.pdf
      this.fillQuoteData(response.result.entityData);
    }
    this.loading = false;
  },

  computed: {
    isEsignAccessible() {
      return this.esign.requested && !this.esign.esigned;
    },
    isEsigned() {
      return this.esign.esigned;
    },
    hasSignLink() {
      return this.signLink.trim().length > 0;
    },
    isSigningPreviewReady() {
      return this.hasSignLink && !this.iframeLoading;
    },
    isSigningPreviewInProgress() {
      return this.signLinkGenerationInProgress || (this.hasSignLink && this.iframeLoading);
    }
  },

  watch: {
    base64(newBase64) {
      // Revoke previous URL to prevent memory leaks
      if (this.objectUrl) {
        try { URL.revokeObjectURL(this.objectUrl); } catch (e) { /* noop */ }
        this.objectUrl = "";
      }
      if (newBase64) {
        try {
          const byteCharacters = atob(newBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          this.objectUrl = URL.createObjectURL(blob);
        } catch (e) {
          this.objectUrl = "";
        }
      }
    }
  },

  methods: {
    async sendFeedback() {
      window.location.assign(this.mailto);
    },

    async downloadPdf() {
      PDFPreviewApi.markAsDownloaded(this.token);

      let a = document.createElement("a");
      a.href = this.base64toURL();
      a.download = this.quoteNo + ".pdf";
      a.click();
    },

    async sign(signatory) {
      if (this.signLinkGenerationInProgress) {
        return;
      }
      this.signLinkGenerationInProgress = true;
      try {
        const response = await InitiativeApi.eSign(this.token, signatory);
        if (this.isMobile) {
          window.location.href = response.data.signLink;
          return;
        }
        this.signLink = response.data.signLink;
      } catch (error) {
        console.error("eSign error:", error.response || error.message || error);
      } finally {
        this.signLinkGenerationInProgress = false;
      }
    },

    fillQuoteData(data) {
      this.company = data.company;
      if (this.company) {
        document.title = this.company;
      }
      this.email = data.email;
      this.phone = data.phone;
      this.assignee = data.assignee;
      this.quoteNo = data.quoteNo;
      this.validtill = data.validtill;
      this.lastUpdate = data.lastUpdate;
      this.mailto = `mailto:${this.email}?subject=${encodeURIComponent('Retours sur devis '+this.quoteNo)}`;
      this.esign = data.esign;
    },

    base64toURL() {
      const blob = this.dataURItoBlob(this.base64);
      return URL.createObjectURL(blob);
    }
  },

  beforeMount() {
    this.isMobile = this.isMobileBrowser();
  },
  beforeDestroy() {
    if (this.objectUrl) {
      try { URL.revokeObjectURL(this.objectUrl); } catch (e) { /* noop */ }
      this.objectUrl = "";
    }
  },
}
</script><style lang="scss">@import "@/assets/scss/pages/_quote.scss";</style>